// Mes Favoris - Page pour les ACHETEURS
// Connectée aux vraies APIs

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, ShoppingCart, Trash2, ArrowLeft, Search, Package,
  Star, Store, ChevronRight, Plus, Share2, Bell, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { favoritesAPI, cartAPI, authAPI } from '../../services/buyerAPI';

// Carte produit favori
function FavoriteProductCard({ product, onRemove, onAddToCart, isRemoving }) {
  const [addingToCart, setAddingToCart] = useState(false);

  const handleAddToCart = async () => {
    setAddingToCart(true);
    try {
      await onAddToCart(product);
    } finally {
      setAddingToCart(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all group"
    >
      {/* Image */}
      <div className="relative aspect-square bg-gray-100">
        {product.product_image ? (
          <img src={product.product_image} alt={product.product_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <Package size={48} className="text-gray-300" />
          </div>
        )}
        {/* Badge économie */}
        {product.price_at_add && product.product_price < product.price_at_add && (
          <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-lg">
            -{Math.round((1 - product.product_price / product.price_at_add) * 100)}%
          </span>
        )}
        {/* Badge hausse prix */}
        {product.price_at_add && product.product_price > product.price_at_add && (
          <span className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-lg">
            Prix en hausse
          </span>
        )}
        {/* Bouton supprimer */}
        <button
          onClick={() => onRemove(product)}
          disabled={isRemoving}
          className="absolute top-2 right-2 p-2 bg-white/90 hover:bg-red-50 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
        >
          <Trash2 size={16} className="text-red-500" />
        </button>
      </div>

      {/* Contenu */}
      <div className="p-4">
        <h3 className="font-medium text-gray-900 line-clamp-2 mb-2 min-h-[48px]">
          {product.product_name}
        </h3>

        {/* Prix */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-xl font-bold text-gray-900">{product.product_price.toFixed(2)} €</span>
          {product.price_at_add && product.price_at_add !== product.product_price && (
            <span className="text-sm text-gray-400 line-through">{product.price_at_add.toFixed(2)} €</span>
          )}
        </div>

        {/* Vendeur */}
        <div className="flex items-center gap-2 mb-4">
          <Store size={14} className="text-gray-400" />
          <span className="text-sm text-gray-500">{product.seller_name}</span>
        </div>

        {/* Date d'ajout */}
        <p className="text-xs text-gray-400 mb-3">
          Ajouté le {new Date(product.created_at).toLocaleDateString('fr-FR')}
        </p>

        {/* Bouton */}
        <Button
          onClick={handleAddToCart}
          className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl"
          disabled={addingToCart}
        >
          {addingToCart ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
          ) : (
            <ShoppingCart size={16} className="mr-2" />
          )}
          Ajouter au panier
        </Button>
      </div>
    </motion.div>
  );
}

export default function MesFavorisPage() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Vérifier l'authentification
    if (!authAPI.isAuthenticated()) {
      toast.error('Connecte-toi pour voir tes favoris');
      navigate('/connexion');
      return;
    }
    fetchFavorites();
  }, [navigate]);

  const fetchFavorites = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await favoritesAPI.getAll();
      setFavorites(data);
    } catch (e) {
      console.error('Error fetching favorites:', e);
      setError(e.message);
      if (e.message.includes('401') || e.message.includes('authentifié')) {
        toast.error('Session expirée, reconnecte-toi');
        navigate('/connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (product) => {
    setRemovingId(product.product_id);
    try {
      await favoritesAPI.remove(product.product_id);
      setFavorites(favorites.filter(f => f.product_id !== product.product_id));
      toast.success(`${product.product_name} retiré des favoris`);
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setRemovingId(null);
    }
  };

  const handleAddToCart = async (product) => {
    try {
      await cartAPI.addItem({
        id: product.product_id,
        name: product.product_name,
        price: product.product_price,
        image: product.product_image,
        seller_id: product.seller_id,
        seller_name: product.seller_name
      });
      toast.success(`${product.product_name} ajouté au panier`);
    } catch (e) {
      toast.error('Erreur lors de l\'ajout au panier');
    }
  };

  const handleAddAllToCart = async () => {
    const toAdd = favorites.filter(f => f.product_id);
    let added = 0;
    
    for (const product of toAdd) {
      try {
        await cartAPI.addItem({
          id: product.product_id,
          name: product.product_name,
          price: product.product_price,
          image: product.product_image,
          seller_id: product.seller_id,
          seller_name: product.seller_name
        });
        added++;
      } catch (e) {
        console.error('Error adding to cart:', e);
      }
    }
    
    if (added > 0) {
      toast.success(`${added} article(s) ajouté(s) au panier`);
    } else {
      toast.error('Erreur lors de l\'ajout au panier');
    }
  };

  const filteredFavorites = favorites.filter(f =>
    searchQuery === '' || (f.product_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalValue = favorites.reduce((sum, f) => sum + (f.product_price || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mes-favoris-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Heart size={24} className="text-red-500 fill-red-500" />
                  Mes favoris
                </h1>
                <p className="text-sm text-gray-500">{favorites.length} article(s) • {totalValue.toFixed(2)} €</p>
              </div>
            </div>
            {favorites.length > 0 && (
              <Button
                onClick={handleAddAllToCart}
                className="bg-orange-500 hover:bg-orange-600 rounded-xl hidden sm:flex"
              >
                <ShoppingCart size={16} className="mr-2" />
                Tout ajouter au panier
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <span className="text-red-700">{error}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchFavorites}
              className="ml-auto text-red-600 hover:text-red-700"
            >
              Réessayer
            </Button>
          </div>
        )}

        {/* Recherche */}
        {favorites.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Rechercher dans mes favoris..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl"
              />
            </div>
          </div>
        )}

        {/* Contenu */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
          </div>
        ) : favorites.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <Heart size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-bold text-gray-900 mb-2">Aucun favori</h3>
            <p className="text-gray-500 mb-6">Ajoute des produits à tes favoris pour les retrouver ici</p>
            <Button 
              onClick={() => navigate('/produits')}
              className="bg-orange-500 hover:bg-orange-600 rounded-xl"
            >
              Découvrir nos produits
            </Button>
          </div>
        ) : filteredFavorites.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <Search size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-bold text-gray-900 mb-2">Aucun résultat</h3>
            <p className="text-gray-500">Aucun produit ne correspond à ta recherche</p>
          </div>
        ) : (
          <>
            {/* Grille de produits */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <AnimatePresence>
                {filteredFavorites.map((product) => (
                  <FavoriteProductCard
                    key={product.id || product.product_id}
                    product={product}
                    onRemove={handleRemove}
                    onAddToCart={handleAddToCart}
                    isRemoving={removingId === product.product_id}
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* Footer mobile */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 sm:hidden">
              <Button
                onClick={handleAddAllToCart}
                className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl"
              >
                <ShoppingCart size={16} className="mr-2" />
                Tout ajouter au panier ({totalValue.toFixed(2)} €)
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
